import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  adminAddHolding, adminDeleteHolding, adminBulkHoldings, adminBulkValuations, adminImportHistory,
  adminGetHoldingDocuments, adminUploadHoldingDocument, adminDeleteDocument,
} from '../../lib/api';
import { money, inputCls } from './shared';

const EMPTY_H = { investorEmail: '', investorName: '', projectId: '', units: 0, initialValue: 0, currentValue: 0 };

const parseCsv = (text, mapper) => {
  const rows = [];
  for (const line of text.split('\n').map((l) => l.trim()).filter(Boolean)) {
    const cells = line.split(',').map((x) => (x || '').trim());
    if (!cells[0] || cells[0].toLowerCase() === 'investoremail') continue;
    rows.push(mapper(cells));
  }
  return rows;
};

export default function HoldingsTab({ projects, holdings, onChanged }) {
  const [hForm, setHForm] = useState(EMPTY_H);
  const [csv, setCsv] = useState('');
  const [valCsv, setValCsv] = useState('');
  const [histCsv, setHistCsv] = useState('');
  const [docHolding, setDocHolding] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [docTitle, setDocTitle] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const addHolding = async (e) => {
    e.preventDefault();
    if (!hForm.investorEmail || !hForm.projectId) return toast.error('Investor email and project are required');
    try {
      await adminAddHolding({ ...hForm, units: Number(hForm.units), initialValue: Number(hForm.initialValue), currentValue: Number(hForm.currentValue) });
      toast.success('Holding added'); setHForm(EMPTY_H); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const removeHolding = async (h) => {
    if (!window.confirm('Delete this holding?')) return;
    try { await adminDeleteHolding(h.id); toast.success('Deleted'); onChanged(); } catch { toast.error('Failed'); }
  };
  const uploadCsv = async () => {
    const rows = parseCsv(csv, ([investorEmail, projectId, units, initialValue, currentValue]) => ({
      investorEmail, projectId, units: Number(units) || 0, initialValue: Number(initialValue) || 0, currentValue: Number(currentValue) || 0,
    }));
    if (rows.length === 0) return toast.error('No valid rows found');
    try { const r = await adminBulkHoldings(rows); toast.success(`Imported ${r.created} holding(s)${r.errors?.length ? `, ${r.errors.length} error(s)` : ''}`); setCsv(''); onChanged(); }
    catch { toast.error('Bulk import failed'); }
  };
  const uploadValuations = async () => {
    const rows = parseCsv(valCsv, ([investorEmail, projectId, currentValue]) => ({ investorEmail, projectId, currentValue: Number(currentValue) || 0 }));
    if (rows.length === 0) return toast.error('No valid rows found');
    try { const r = await adminBulkValuations(rows); toast.success(`Updated ${r.updated} valuation(s)${r.errors?.length ? `, ${r.errors.length} error(s)` : ''}`); setValCsv(''); onChanged(); }
    catch { toast.error('Valuation update failed'); }
  };
  const uploadHistory = async () => {
    const rows = parseCsv(histCsv, ([investorEmail, projectId, date, value]) => ({ investorEmail, projectId, date, value: Number(value) || 0 }));
    if (rows.length === 0) return toast.error('No valid rows found');
    try { const r = await adminImportHistory(rows); toast.success(`Imported ${r.updated} history point(s)${r.errors?.length ? `, ${r.errors.length} error(s)` : ''}`); setHistCsv(''); onChanged(); }
    catch { toast.error('History import failed'); }
  };

  const openDocs = async (h) => {
    setDocHolding(h); setDocFile(null); setDocTitle('');
    try { setDocs(await adminGetHoldingDocuments(h.id)); } catch { setDocs([]); }
  };
  const uploadDoc = async () => {
    if (!docFile) return toast.error('Choose a file first');
    if (docFile.size > 10 * 1024 * 1024) return toast.error('File must be under 10 MB');
    setUploadingDoc(true);
    try {
      await adminUploadHoldingDocument(docHolding.id, docFile, docTitle);
      toast.success('Document uploaded');
      setDocFile(null); setDocTitle('');
      setDocs(await adminGetHoldingDocuments(docHolding.id));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDoc(false); }
  };
  const removeDoc = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try { await adminDeleteDocument(d.id); setDocs(await adminGetHoldingDocuments(docHolding.id)); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={addHolding} className="space-y-3 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
          <h3 className="font-display font-bold text-farmway-cream">Add a holding</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Investor email</Label><Input value={hForm.investorEmail} onChange={(e) => setHForm({ ...hForm, investorEmail: e.target.value })} className={inputCls} /></div>
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Farm / project</Label>
              <select value={hForm.projectId} onChange={(e) => setHForm({ ...hForm, projectId: e.target.value })} className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-farmway-cream focus:outline-none focus:ring-2 focus:ring-farmway-gold">
                <option value="" className="bg-farmway-green-dark">Select…</option>
                {projects.map((p) => <option key={p.id} value={p.id} className="bg-farmway-green-dark">{p.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Units</Label><Input type="number" value={hForm.units} onChange={(e) => setHForm({ ...hForm, units: e.target.value })} className={inputCls} /></div>
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Initial value</Label><Input type="number" value={hForm.initialValue} onChange={(e) => setHForm({ ...hForm, initialValue: e.target.value })} className={inputCls} /></div>
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Current value</Label><Input type="number" value={hForm.currentValue} onChange={(e) => setHForm({ ...hForm, currentValue: e.target.value })} className={inputCls} /></div>
          </div>
          <button type="submit" className="btn-gold w-full"><Plus className="h-4 w-4" /> Add holding</button>
        </form>
        <div className="space-y-3 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
          <h3 className="font-display font-bold text-farmway-cream">Bulk upload (CSV)</h3>
          <p className="text-xs text-farmway-cream/50">One per line: <code className="text-farmway-gold">investorEmail,projectId,units,initialValue,currentValue</code></p>
          <Textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={'jane@email.com,geo-almond-1,10,5000,6200'} className={`resize-none font-mono text-xs ${inputCls}`} />
          <button onClick={uploadCsv} className="btn-gold w-full"><Upload className="h-4 w-4" /> Import CSV</button>
        </div>
      </div>
      <div className="mt-6 space-y-3 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
        <h3 className="font-display font-bold text-farmway-cream">Update valuations (CSV)</h3>
        <p className="text-xs text-farmway-cream/50">One per line: <code className="text-farmway-gold">investorEmail,projectId,currentValue</code>. Refreshes each holding&apos;s current value and value-history chart.</p>
        <Textarea data-testid="val-csv" rows={4} value={valCsv} onChange={(e) => setValCsv(e.target.value)} placeholder={'jane@email.com,geo-almond-1,7900'} className={`resize-none font-mono text-xs ${inputCls}`} />
        <button data-testid="update-valuations-btn" onClick={uploadValuations} className="btn-gold w-full"><Upload className="h-4 w-4" /> Update valuations</button>
      </div>
      <div className="mt-6 space-y-3 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
        <h3 className="font-display font-bold text-farmway-cream">Import valuation history (CSV)</h3>
        <p className="text-xs text-farmway-cream/50">One per line: <code className="text-farmway-gold">investorEmail,projectId,date,value</code>. Adds dated points (e.g. <code className="text-farmway-gold">2024-01-31</code>) so holding charts show true history. Rows are sorted by date and set the current value to the latest point.</p>
        <Textarea data-testid="hist-csv" rows={5} value={histCsv} onChange={(e) => setHistCsv(e.target.value)} placeholder={'jane@email.com,geo-almond-1,2024-01-31,5200\njane@email.com,geo-almond-1,2024-06-30,6100'} className={`resize-none font-mono text-xs ${inputCls}`} />
        <button data-testid="import-history-btn" onClick={uploadHistory} className="btn-gold w-full"><Upload className="h-4 w-4" /> Import history</button>
      </div>
      <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
        {holdings.length === 0 ? <p className="px-6 py-8 text-farmway-cream/50">No holdings recorded.</p> : holdings.map((h) => (
          <div key={h.id} className="grid grid-cols-2 items-center gap-4 border-t border-white/10 px-6 py-4 first:border-t-0 md:grid-cols-6">
            <div className="col-span-2"><p className="text-sm font-medium text-farmway-cream">{h.projectName}</p><p className="text-xs text-farmway-cream/50">{h.investorEmail}</p></div>
            <p className="text-sm text-farmway-cream/70">{h.units} units</p>
            <p className="text-sm text-farmway-cream/70">{money(h.initialValue)}</p>
            <p className="text-sm text-farmway-cream/70">{money(h.currentValue)}{h.listed && <span className="ml-2 text-[10px] uppercase text-farmway-gold">listed</span>}</p>
            <div className="flex items-center justify-end gap-2">
              <button data-testid={`holding-docs-btn-${h.id}`} onClick={() => openDocs(h)} title="Documents" className="grid h-8 w-8 place-items-center rounded-lg border border-white/15 text-farmway-cream/70 hover:text-farmway-gold"><FileText className="h-4 w-4" /></button>
              <button onClick={() => removeHolding(h)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/15 text-farmway-cream/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(docHolding)} onOpenChange={(o) => !o && setDocHolding(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-farmway-green-dark text-farmway-cream">
          <DialogHeader>
            <span className="eyebrow">Documents</span>
            <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">{docHolding?.projectName}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-farmway-cream/50">{docHolding?.investorEmail} · certificates are auto-generated; upload signed agreements or other files here.</p>
          <div className="space-y-2">
            {docs.length === 0 ? <p className="rounded-lg bg-white/5 px-4 py-3 text-sm text-farmway-cream/50">No documents uploaded yet.</p> : docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 px-4 py-2.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-farmway-cream/85"><FileText className="h-4 w-4 shrink-0 text-farmway-leaf-bright" /><span className="truncate">{d.title}</span></span>
                <button data-testid={`admin-doc-delete-${d.id}`} onClick={() => removeDoc(d)} className="shrink-0 text-farmway-cream/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-3 border-t border-white/10 pt-4">
            <div className="space-y-1.5"><Label className="text-farmway-cream/80">Title (optional)</Label><Input data-testid="admin-doc-title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Signed investment agreement" className={inputCls} /></div>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/20 bg-white/5 px-4 py-3 text-sm text-farmway-cream/70 transition-colors hover:border-farmway-gold/60">
              <Upload className="h-4 w-4 shrink-0 text-farmway-gold" />
              <span className="truncate">{docFile ? docFile.name : 'Choose a file (JPG, PNG or PDF, max 10 MB)'}</span>
              <input data-testid="admin-doc-file" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
            </label>
            <button data-testid="admin-doc-upload" onClick={uploadDoc} disabled={uploadingDoc} className="btn-gold w-full disabled:opacity-70"><Upload className="h-4 w-4" /> {uploadingDoc ? 'Uploading…' : 'Upload document'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
